import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  signal,
  OnInit,
  effect,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, switchMap, of, catchError } from 'rxjs';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  FormsModule,
} from '@angular/forms';
import { AngularRenderer, TiptapEditorDirective } from 'ngx-tiptap';
import { Editor as TipTapEditor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { CharacterCount, Focus, Placeholder } from '@tiptap/extensions';
import TextAlign from '@tiptap/extension-text-align';
import Mention from '@tiptap/extension-mention';
import { SuggestionProps } from '@tiptap/suggestion';
import { TableKit } from '@tiptap/extension-table';
import tippy, { Instance, Props } from 'tippy.js';
import { MentionListComponent } from './mention-list/mention-list.component';
import { TiptapEditorMenuComponent } from './tiptap-editor-menu/tiptap-editor-menu.component';
import {
  MentionedUser,
  MentionListResponse,
  MentionSearchFn,
} from './mention.model';
import { INIT_MENTION_VALUE } from './mention.constants';

@Component({
  selector: 'lib-tiptap-rich-editor',
  imports: [TiptapEditorDirective, FormsModule, TiptapEditorMenuComponent],
  templateUrl: './tiptap-rich-editor.component.html',
  styleUrl: './tiptap-rich-editor.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TiptapRichEditorComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly parentContainer = inject(ControlContainer);
  private readonly destroyRef = inject(DestroyRef);

  inputFormControlName = input.required<string>();
  maxLength = input(6000);
  label = input<string>();
  required = input(false);
  disabled = input(false);
  placeholder = input<string>();
  canResize = input(true);
  isDualLangEnabled = input<boolean>(false); // Assuming this would be set based on inputs

  // Mention configuration inputs
  enableMentions = input(false);
  mentionSearchFn = input<MentionSearchFn | undefined>();

  editorInstance = output<TipTapEditor>();
  mentions = output<number[]>();

  characterCount = signal(0);
  private readonly isUpdatingFromEditor = signal(false);
  private readonly mentionSearch$ = new Subject<string>();
  private mentionResolve: ((results: MentionedUser[]) => void) | null = null;
  private readonly MENTION_DEBOUNCE_MS = 300;
  currentPage = signal(1);
  meta = signal(INIT_MENTION_VALUE.meta);
  isLoadingMore = signal(false);

  // Accumulated items for pagination
  private readonly accumulatedItems = signal<MentionedUser[]>([]);
  // Store renderer reference for manual updates during pagination
  private mentionRenderer: AngularRenderer<
    MentionListComponent,
    MentionListComponent
  > | null = null;
  private currentMentionProps: SuggestionProps | null = null;

  constructor() {
    // Listen to disabled input changes and update editor
    effect(() => {
      const isDisabled = this.disabled();
      if (this.editor) {
        this.editor.setEditable(!isDisabled);
      }
    });
  }

  get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  get childControl() {
    return this.parentFormGroup?.controls[
      this.inputFormControlName()
    ] as FormControl;
  }

  editor?: TipTapEditor;

  ngOnInit(): void {
    this.setupMentionSearch();
    this.createEditor();
    this.setupFormControlSync();
  }

  private setupMentionSearch(): void {
    this.mentionSearch$
      .pipe(
        debounceTime(this.MENTION_DEBOUNCE_MS),
        switchMap(query => {
          const searchFn = this.mentionSearchFn();
          if (!searchFn) {
            return of(INIT_MENTION_VALUE);
          }
          return searchFn(query, this.currentPage()).pipe(
            catchError(() => of(INIT_MENTION_VALUE))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(results => {
        this.meta.set(results.meta);

        // Accumulate items for pagination
        if (this.currentPage() > 1) {
          // Append new items to existing ones
          const newItems = [...this.accumulatedItems(), ...results.data];
          this.accumulatedItems.set(newItems);
        } else {
          // First page - reset accumulated items
          this.accumulatedItems.set(results.data);
        }

        // Resolve initial promise if it exists (first page load)
        if (this.mentionResolve) {
          this.mentionResolve(this.accumulatedItems());
          this.mentionResolve = null;
        }

        // Update renderer with new items (for pagination updates)
        if (this.mentionRenderer && this.currentMentionProps) {
          const updatedProps = {
            ...this.currentMentionProps,
            items: this.accumulatedItems(),
          };
          this.isLoadingMore.set(false);
          this.mentionRenderer.updateProps({
            props: updatedProps,
            meta: this.meta(),
            isLoading: false,
          } as unknown as Partial<MentionListComponent>);
        }
      });
  }

  private createEditor(): void {
    this.editor = new TipTapEditor({
      content: this.childControl.value || '',
      extensions: this.buildExtensions(),
      editorProps: {
        attributes: {
          class: 'h-full overflow-auto p-2xl focus:outline-none leading-xl',
        },
      },
      onUpdate: ({ editor }) => {
        this.isUpdatingFromEditor.set(true);
        const html = editor.getHTML();
        const characters = editor.storage.characterCount.characters();
        const isEmpty = editor.getText().trim().length === 0;
        this.childControl?.setValue(isEmpty || characters === 0 ? null : html);
        this.characterCount.set(isEmpty ? 0 : characters);
        this.isUpdatingFromEditor.set(false);
      },
      onBlur: () => {
        if (!this.childControl?.touched) {
          this.childControl?.markAsTouched();
        }
      },
    });

    this.editorInstance.emit(this.editor);
  }

  private setupFormControlSync(): void {
    if (!this.childControl || !this.editor) {
      return;
    }

    // Sync form control value changes to editor (only external changes)
    this.childControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (!this.isUpdatingFromEditor()) {
          this.editor?.commands.setContent(value || '', { emitUpdate: false });
          this.characterCount.set(
            this.editor?.storage.characterCount.characters() || 0
          );
          if (this.enableMentions()) {
            this.mentions.emit(this.getMentions());
          }
        }
      });

    // Set initial disabled state
    const initialDisabled = this.childControl.disabled || this.disabled();
    this.editor?.setEditable(!initialDisabled);
  }

  private buildExtensions() {
    const extensions = [
      StarterKit.configure({
        link: {
          autolink: false,
          defaultProtocol: 'https',
          protocols: ['http', 'https'],
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      CharacterCount.configure({
        textCounter: text => text.replace(/\s/g, '').length,
        limit: this.maxLength(),
      }),
      Placeholder.configure({
        placeholder: this.placeholder(),
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: {
          allowSpaces: true,
          items: ({ query }) => {
            if (!this.enableMentions() || !this.mentionSearchFn()) {
              return Promise.resolve([]);
            }

            // Resolve any pending promise with empty array to avoid stale promises
            if (this.mentionResolve) {
              this.mentionResolve(INIT_MENTION_VALUE.data);
            }

            // Reset pagination for new query
            this.currentPage.set(1);
            this.accumulatedItems.set([]);

            return new Promise<MentionListResponse['data']>(resolve => {
              this.mentionResolve = resolve;
              this.mentionSearch$.next(query);
            });
          },
          render: () => this.createMentionRenderer(),
        },
      }),
      TableKit.configure({
        table: {
          resizable: true,
          renderWrapper: true,
        },
      }),
      Focus.configure({
        className: 'has-focus',
        mode: 'deepest',
      }),
    ];

    return extensions;
  }

  private createMentionRenderer() {
    let renderer: AngularRenderer<MentionListComponent, MentionListComponent>;
    let popup: Instance<Props>;

    return {
      onStart: (props: SuggestionProps) => {
        renderer = new AngularRenderer(MentionListComponent, this.injector, {
          props,
          meta: this.meta(),
        });

        this.mentionRenderer = renderer;
        this.currentMentionProps = props;

        renderer.updateProps({
          props,
          meta: this.meta(),
        } as unknown as Partial<MentionListComponent>);

        renderer.instance.pageChange.subscribe(page => {
          this.currentPage.set(page);
          this.isLoadingMore.set(true);
          renderer.updateProps({
            props,
            meta: this.meta(),
            isLoading: true,
          } as unknown as Partial<MentionListComponent>);
          this.mentionSearch$.next(props.query);
        });

        // Determine placement based on document direction for RTL support
        const isRtl = document.documentElement.dir === 'rtl';
        const placement = isRtl ? 'top-end' : 'top-start';

        popup = tippy(document.body, {
          getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(),
          appendTo: () => document.body,
          content: renderer.dom,
          showOnCreate: props.items?.length > 0,
          interactive: true,
          trigger: 'manual',
          placement,
          onMount: instance => {
            // Prevent clicks from bubbling to document and triggering offcanvas close
            const tippyBox = instance.popper.querySelector(
              '.tippy-box'
            ) as HTMLElement;
            if (tippyBox) {
              // Add data attribute that offcanvas/modals can check
              tippyBox.dataset['mentionPopup'] = 'true';
            }
          },
        });
      },
      onUpdate: (props: SuggestionProps) => {
        this.currentMentionProps = props;
        renderer.updateProps({
          props,
          meta: this.meta(),
        } as unknown as Partial<MentionListComponent>);

        popup.setProps({
          getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(),
        });

        // Show/hide popup based on items availability
        if (this.enableMentions() && props.items?.length > 0) {
          popup.show();
        } else {
          popup.hide();
        }
      },
      onExit: () => {
        this.mentions.emit(this.getMentions());
        this.mentionRenderer = null;
        this.currentMentionProps = null;
        this.currentPage.set(1);
        this.accumulatedItems.set([]);
        popup?.destroy();
        renderer?.destroy();
      },
    };
  }

  getMentions() {
    const mentions: unknown[] = [];
    this.editor?.state.doc.descendants(node => {
      if (node.type.name === 'mention') {
        mentions.push(node.attrs);
      }
    });
    return (mentions as MentionedUser[]).map(mention => mention.id);
  }

  ngOnDestroy(): void {
    this.mentionSearch$.complete();
    this.editor?.destroy();
  }
}
